import React, { useCallback, useEffect, useRef, useState } from "react";
import "./CustomColorPicker.css";

const POPULAR_PALETTE = [
  "#FFFFFF",
  "#000000",
  "#47c9a9",
  "#007aaf",
  "#c2c2c2",
  "#0000FF",
  "#00FF00",
  "#FF0000",
  "#8B4513",
  "#FFFF00",
  "#FFA500",
  "#FFC0CB",
  "#A0826D",
  "#0000A0",
  "#808080",
];

export function hsvToRgb(h, s, v) {
  s = s / 100;
  v = v / 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

export function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  let h = 0;
  const s = max === 0 ? 0 : (diff / max) * 100;
  const v = max * 100;

  if (diff !== 0) {
    if (max === r) h = 60 * (((g - b) / diff) % 6);
    else if (max === g) h = 60 * ((b - r) / diff + 2);
    else h = 60 * ((r - g) / diff + 4);
  }
  if (h < 0) h += 360;

  return { h, s, v };
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  let h = 0;
  const l = (max + min) / 2;
  const s = diff === 0 ? 0 : diff / (1 - Math.abs(2 * l - 1));

  if (diff !== 0) {
    if (max === r) h = 60 * (((g - b) / diff) % 6);
    else if (max === g) h = 60 * ((b - r) / diff + 2);
    else h = 60 * ((r - g) / diff + 4);
  }
  if (h < 0) h += 360;

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToRgb(h, s, l) {
  s = s / 100;
  l = l / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

function formatRgb(r, g, b) {
  return `rgb(${r}, ${g}, ${b})`;
}

function formatHsl(h, s, l) {
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function parseRgb(str) {
  const match = str.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
    };
  }
  return null;
}

function parseHsl(str) {
  const match = str.match(/hsl\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*\)/);
  if (match) {
    return {
      h: parseInt(match[1], 10),
      s: parseInt(match[2], 10),
      l: parseInt(match[3], 10),
    };
  }
  return null;
}

function normalizeHex(color) {
  if (!color || typeof color !== "string") return "#ffffff";
  const trimmed = color.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const [, a, b, c] = trimmed;
    return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
  }
  const rgb = hexToRgb(trimmed);
  if (rgb) return rgbToHex(rgb.r, rgb.g, rgb.b);
  return "#ffffff";
}

/**
 * Shared HS/hex custom color popup (same UI as Background Color picker).
 *
 * Usage:
 *   <CustomColorPicker
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     color={value}
 *     onApply={(hex) => setValue(hex)}
 *     ignoreCloseSelectors={["#color-picker"]}
 *     anchorSelector=".tool-prep-popup__left"
 *     anchorGap={10}
 *   />
 */
const CustomColorPicker = ({
  isOpen = false,
  onClose = () => {},
  color = "#ffffff",
  onChange = null,
  onApply = () => {},
  initialPosition = { x: 380, y: 200 },
  /** CSS selector for an element to sit beside (e.g. left panel). */
  anchorSelector = null,
  /** Optional: align picker top with this element (e.g. color grid). */
  anchorTopSelector = null,
  /** Gap in px between anchor’s right edge and the picker. */
  anchorGap = 10,
  ignoreCloseSelectors = ["#color-picker"],
  applyLabel = "Apply Color",
  showApplyButton = true,
  showPopularPalette = true,
  /** Override default popular swatches (e.g. watermark presets). */
  popularColors = null,
  /** Render body only — parent owns docking / open state (e.g. WatermarkColorMenu). */
  embedded = false,
  /** Hide drag handle when embedded in another chrome. */
  showDragHeader = true,
}) => {
  const initialHex = normalizeHex(color);
  const initialRgb = hexToRgb(initialHex) || { r: 255, g: 255, b: 255 };
  const initialHsv = rgbToHsv(initialRgb.r, initialRgb.g, initialRgb.b);

  const [hue, setHue] = useState(initialHsv.h);
  const [saturation, setSaturation] = useState(initialHsv.s);
  const [brightness, setBrightness] = useState(initialHsv.v);
  const [colorFormat, setColorFormat] = useState("HEX");
  const [hexInput, setHexInput] = useState(initialHex.toUpperCase());
  const [customColor, setCustomColor] = useState(initialHex);
  const [popupPosition, setPopupPosition] = useState(initialPosition);

  const pickerRef = useRef(null);
  const saturationRef = useRef(null);
  const hueRef = useRef(null);
  const isDraggingSaturation = useRef(false);
  const isDraggingHue = useRef(false);
  const isDraggingPopup = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const syncedColorRef = useRef(initialHex);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const placeBesideAnchor = useCallback(() => {
    if (!anchorSelector) {
      setPopupPosition(initialPosition);
      return;
    }
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) {
      setPopupPosition(initialPosition);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const topEl = anchorTopSelector
      ? document.querySelector(anchorTopSelector)
      : null;
    const topRect = topEl?.getBoundingClientRect?.() || rect;

    const pickerWidth = 320;
    const measuredH = pickerRef.current?.offsetHeight;
    const pickerHeight = measuredH > 0 ? measuredH : 420;
    let x = rect.right + anchorGap;
    let y = topRect.top;

    // Keep on-screen (including Apply button at bottom)
    const maxX = window.innerWidth - pickerWidth - 12;
    const maxY = window.innerHeight - pickerHeight - 12;
    if (x > maxX) {
      // Flip to the left of the panel if no room on the right
      x = Math.max(12, rect.left - pickerWidth - anchorGap);
    }
    y = Math.max(12, Math.min(y, Math.max(12, maxY)));

    setPopupPosition({ x, y });
  }, [anchorGap, anchorSelector, anchorTopSelector, initialPosition]);

  // Sync from parent when opened / color prop changes while closed-open cycle
  useEffect(() => {
    if (!isOpen && !embedded) return;
    if (embedded && !isOpen) return;
    const next = normalizeHex(color);
    if (next === syncedColorRef.current) return;
    syncedColorRef.current = next;
    const rgb = hexToRgb(next);
    if (!rgb) return;
    const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    setHue(hsv.h);
    setSaturation(hsv.s);
    setBrightness(hsv.v);
    setCustomColor(next);
    setHexInput(next.toUpperCase());
  }, [color, isOpen, embedded]);

  useEffect(() => {
    if (!isOpen || embedded) return undefined;
    placeBesideAnchor();
    // Re-measure after paint so Apply stays fully visible
    const raf = requestAnimationFrame(() => placeBesideAnchor());

    if (!anchorSelector) {
      return () => cancelAnimationFrame(raf);
    }
    const onResize = () => placeBesideAnchor();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, embedded, anchorSelector, placeBesideAnchor]);

  useEffect(() => {
    const rgb = hsvToRgb(hue, saturation, brightness);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    setCustomColor((prev) => (prev === hex ? prev : hex));
    syncedColorRef.current = hex;
    onChangeRef.current?.(hex);

    if (colorFormat === "HEX") {
      const next = hex.toUpperCase();
      setHexInput((prev) => (prev === next ? prev : next));
    } else if (colorFormat === "RGB") {
      const next = formatRgb(rgb.r, rgb.g, rgb.b);
      setHexInput((prev) => (prev === next ? prev : next));
    } else if (colorFormat === "HSL") {
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      const next = formatHsl(hsl.h, hsl.s, hsl.l);
      setHexInput((prev) => (prev === next ? prev : next));
    }
  }, [hue, saturation, brightness, colorFormat]);

  const updateSaturationBrightness = useCallback((e) => {
    if (!saturationRef.current) return;
    const rect = saturationRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    setSaturation((x / rect.width) * 100);
    setBrightness(100 - (y / rect.height) * 100);
  }, []);

  const updateHue = useCallback((e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setHue((x / rect.width) * 360);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseMove = (e) => {
      if (isDraggingSaturation.current) updateSaturationBrightness(e);
      if (isDraggingHue.current) updateHue(e);
      if (isDraggingPopup.current) {
        setPopupPosition({
          x: e.clientX - dragOffset.current.x,
          y: e.clientY - dragOffset.current.y,
        });
      }
    };

    const handleMouseUp = () => {
      isDraggingSaturation.current = false;
      isDraggingHue.current = false;
      isDraggingPopup.current = false;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isOpen, updateHue, updateSaturationBrightness]);

  useEffect(() => {
    if (!isOpen || embedded) return undefined;

    const handleClickOutside = (e) => {
      if (isDraggingPopup.current) return;
      if (pickerRef.current?.contains(e.target)) return;
      for (const sel of ignoreCloseSelectors) {
        if (typeof sel === "string" && e.target.closest?.(sel)) return;
      }
      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, embedded, ignoreCloseSelectors, onClose]);

  const handleHexInputChange = (e) => {
    let value = e.target.value;
    setHexInput(value);

    let rgb = null;
    if (colorFormat === "HEX") {
      if (!value.startsWith("#")) value = `#${value}`;
      if (/^#[0-9A-Fa-f]{6}$/.test(value)) rgb = hexToRgb(value);
    } else if (colorFormat === "RGB") {
      rgb = parseRgb(value);
    } else if (colorFormat === "HSL") {
      const hsl = parseHsl(value);
      if (hsl) rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    }

    if (rgb) {
      const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
      setHue(hsv.h);
      setSaturation(hsv.s);
      setBrightness(hsv.v);
      setCustomColor(rgbToHex(rgb.r, rgb.g, rgb.b));
    }
  };

  const applyPopular = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return;
    const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    setHue(hsv.h);
    setSaturation(hsv.s);
    setBrightness(hsv.v);
  };

  const handleApply = () => {
    onApply(customColor);
    onClose();
  };

  const handlePopupMouseDown = (e) => {
    if (
      e.target.classList.contains("custom-color-picker__header") ||
      e.target.closest(".custom-color-picker__header")
    ) {
      isDraggingPopup.current = true;
      const rect = pickerRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!isOpen) return null;

  const palette = Array.isArray(popularColors) && popularColors.length
    ? popularColors
    : POPULAR_PALETTE;

  const paletteSection =
    showPopularPalette ? (
      <div className="custom-color-picker__popular popular-palette-section">
        <p className="custom-color-picker__popular-title popular-palette-title">
          Most Popular Palette
        </p>
        <div className="custom-color-picker__popular-grid popular-palette-grid">
          {palette.map((swatch) => (
            <button
              key={swatch}
              type="button"
              className="custom-color-picker__popular-swatch popular-palette-color"
              style={{ backgroundColor: swatch }}
              onClick={() => applyPopular(swatch)}
              aria-label={`Select ${swatch}`}
            />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div
      className={`custom-color-picker custom-color-popup${
        embedded ? " custom-color-picker--embedded" : ""
      }`}
      ref={pickerRef}
      style={
        embedded
          ? undefined
          : {
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
            }
      }
      role="dialog"
      aria-label="Custom color picker"
    >
      {showDragHeader && !embedded ? (
        <div
          className="custom-color-picker__header popup-header"
          onMouseDown={handlePopupMouseDown}
        />
      ) : null}

      {embedded ? paletteSection : null}

      <div
        ref={saturationRef}
        className="custom-color-picker__sb saturation-brightness-picker"
        style={{
          background: `linear-gradient(to bottom, transparent, black),
                            linear-gradient(to right, white, hsl(${hue}, 100%, 50%))`,
        }}
        onMouseDown={(e) => {
          isDraggingSaturation.current = true;
          updateSaturationBrightness(e);
        }}
      >
        <div
          className="custom-color-picker__cursor picker-cursor"
          style={{
            left: `${saturation}%`,
            top: `${100 - brightness}%`,
          }}
        />
      </div>

      <div
        ref={hueRef}
        className="custom-color-picker__hue hue-slider"
        onMouseDown={(e) => {
          isDraggingHue.current = true;
          updateHue(e);
        }}
      >
        <div
          className="custom-color-picker__hue-cursor hue-cursor"
          style={{ left: `${(hue / 360) * 100}%` }}
        />
      </div>

      <div className="custom-color-picker__input-row color-input-row">
        <select
          className="custom-color-picker__format format-selector"
          value={colorFormat}
          onChange={(e) => setColorFormat(e.target.value)}
        >
          <option value="HEX">HEX</option>
          <option value="RGB">RGB</option>
          <option value="HSL">HSL</option>
        </select>
        <input
          type="text"
          className="custom-color-picker__hex hex-input"
          placeholder={
            colorFormat === "HEX"
              ? "#FFFFFF"
              : colorFormat === "RGB"
                ? "rgb(255, 255, 255)"
                : "hsl(0, 0%, 100%)"
          }
          value={hexInput}
          onChange={handleHexInputChange}
        />
      </div>

      {!embedded ? paletteSection : null}

      {showApplyButton ? (
        <button
          type="button"
          className="custom-color-picker__apply picker-apply-btn"
          onClick={handleApply}
        >
          {applyLabel}
        </button>
      ) : null}
    </div>
  );
};

export default CustomColorPicker;
